import React, { useState, useEffect } from 'react';
import { X, Heart, Calendar, MapPin } from 'lucide-react';
import { matchApi } from '../lib/api';
import type { Tables } from '../lib/types';

interface ViewMatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant: Tables<'contacts'>;
}

const ViewMatchesModal: React.FC<ViewMatchesModalProps> = ({ isOpen, onClose, participant }) => {
  const [participantMatches, setParticipantMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && participant?.id) {
      loadParticipantMatches();
    }
  }, [isOpen, participant]);

  const loadParticipantMatches = async () => {
    try {
      setLoading(true);
      setError(null);

      // Carica tutti i match
      const allMatches = await matchApi.getAll();

      // Filtra solo i match reciproci (mutual = true)
      const mutualMatches = allMatches.filter(match => match.mutual);

      // Filtra i match del partecipante corrente e rimuovi i duplicati
      const uniqueMatches = mutualMatches.reduce((acc: any[], match) => {
        // Verifica se il partecipante è coinvolto nel match
        const isParticipant1 = match.participant1_id === participant.id;
        const isParticipant2 = match.participant2_id === participant.id;
        
        if (!isParticipant1 && !isParticipant2) return acc;

        // Crea una chiave unica per il match ordinando gli ID dei partecipanti
        const [smaller, larger] = [match.participant1_id, match.participant2_id].sort();
        const matchKey = `${smaller}-${larger}`;

        // Verifica se abbiamo già processato questo match
        const existingMatchIndex = acc.findIndex(m => {
          const [mSmaller, mLarger] = [m.participant1_id, m.participant2_id].sort();
          return `${mSmaller}-${mLarger}` === matchKey;
        });

        if (existingMatchIndex !== -1) return acc;

        // Determina quale partecipante è il match
        const matchedParticipant = isParticipant1 ? match.participant2 : match.participant1;

        // Aggiungi il match all'accumulatore solo se è reciproco
        acc.push({
          id: match.id,
          name: matchedParticipant?.name?.split(' ')[0] || 'Partecipante non trovato',
          gender: matchedParticipant?.gender || 'N/A',
          status: match.status
        });

        return acc;
      }, []);

      setParticipantMatches(uniqueMatches);
    } catch (error: any) {
      console.error('Error loading matches:', error);
      setError(error.message || 'Errore nel caricamento dei match');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !participant) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">Match di {participant.name?.split(' ')[0]}</h2>
            <p className="text-gray-600">Match reciproci confermati</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Caricamento match in corso...</p>
          </div>
        ) : participantMatches.length > 0 ? (
          <div className="space-y-4">
            {participantMatches.map((match) => (
              <div 
                key={match.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-50 rounded-full">
                    <Heart className="h-5 w-5 text-rose-500" />
                  </div>
                  <div>
                    <h3 className="font-medium">{match.name}</h3>
                    <div className="text-sm text-gray-500 capitalize">
                      {({'uomo_etero': 'Uomo Etero', 'donna_etero': 'Donna Etero', 'uomo_gay': 'Uomo Gay', 'donna_gay': 'Donna Gay', 'non_dichiarato': 'Non dichiarato'} as Record<string, string>)[match.gender] || match.gender}
                    </div>
                  </div>
                </div>
                <div>
                  <span className={`px-2 py-1 rounded-full text-sm ${
                    match.status === 'accepted' 
                      ? 'bg-green-100 text-green-800'
                      : match.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {match.status === 'accepted' ? 'Accettato' : 
                     match.status === 'rejected' ? 'Rifiutato' : 'In attesa'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Heart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nessun match reciproco trovato</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewMatchesModal;